<template>
  <li :id="`cart-item-${cartItem.sku}`">
    <div class="flex-start mb">
      <div class="no-shrink mr-quarter">
        <svg-icon-component :icon="getIcon(cartItem.sku)" class="icon-native-fill" />
      </div>
      <div class="grow mr-half truncate">
        <h3 :id="createId('name')" class="mb-half">
          {{ t(`Brand.${isStandAloneWebrootSku ? "WSAV" : cartItem.sku}`) }}
        </h3>
        <NotableFeature :cart-item="props.cartItem" />
        <select
          v-if="showSelect()"
          id="selectPlanLength"
          v-model="selectedRatePlanId"
          class="select-css constrain-40 mb-half"
        >
          <option v-for="r in orderedRatePlans" :key="r.ratePlanId" :value="r.ratePlanId">
            {{ getTermDescriptionForCatalog(r) }}
          </option>
        </select>
        <p v-else-if="!isStandAloneWebrootSku" id="singleRatePlan">
          {{ getTermDescriptionForCatalog(selectedRatePlan) }}
        </p>
        <div v-if="showStepper(cartItem)" class="flex-start align-items-center">
          <div class="mr-half">{{ tn("qty") }}</div>
          <QuantityStepper
            :initial-number="quantity"
            :sku="cartItem.sku"
            @increase="quantity += 1"
            @decrease="quantity += -1"
          />
        </div>
        <div v-else-if="buyFlowStore.getMaxQuantityBySku(cartItem.sku) === 1 && !isWebrootSku">
          <div class="mr-half">{{ tn("maxQuantityOfOne") }}</div>
        </div>
        <div v-else-if="fromCarbonite">
          <div class="mr-half">{{ tn("quantity") }} {{ quantity }}</div>
        </div>
        <div
          v-if="
            isWebrootSku &&
            FLAGS.ENABLE_BUYFLOW_WEBROOT &&
            !isStandAloneWebrootSku &&
            buyFlowStore.pageState === pageStates.email
          "
        >
          <button
            :id="`btnRemove${cartItem.sku}`"
            class="btn-link left-link"
            type="button"
            @click="buyFlowStore.removeFromCart(props.cartItem, props.index)"
          >
            {{ t("Common.remove") }}
          </button>
        </div>
      </div>
      <div class="amount">
        <span :id="createId('itemSubtotal')">{{ formatMoney(cartItem.total || 0) }}</span>
        <span
          v-if="!buyFlowStore.isRenewOrUpgrade && showStrikeThroughPrice"
          :id="createId('strikeThroughPrice')"
          class="strike text-sm text-grey block text-right"
        >
          {{ formatMoney(strikeThroughPrice) }}
        </span>
      </div>
    </div>
    <div class="indent">
      <BundleItems :bundle-items="bundledItems" :cart-item="props.cartItem" />
      <Adjustments :adjustments="cartItem.adjustments || []" />
    </div>
  </li>
</template>

<script setup lang="ts">
import { ICartItem } from "@/common/api/unifiedPortal/interfaces";
import { terms } from "@/components/Buy/BuyEnums";
import { computed, PropType, ref, watch, onMounted } from "vue";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { t } from "@/i18n";
import { formatMoney } from "@/components/Buy/commonFn";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import Adjustments from "@/components/Buy/Adjustments.vue";
import QuantityStepper from "@/components/Buy/QuantityStepper.vue";
import { DEFAULT_TERM_LENGTH_MONTHS, NUMERIC_YEAR } from "@/define";
import { FLAGS } from "@/define";
import BundleItems from "@/components/Buy/Cart/BundleItems.vue";
import { isMobile } from "@/common/browserDetect";
import { getComputerName } from "@/components/Buy/BuyHelper";
import NotableFeature from "@/components/Buy/Cart/NotableFeature.vue";
import { debounce } from "@/common/helper";
import { pageStates } from "@/components/Buy/BuyEnums";
import { useRoute } from "vue-router";
import { IProductIcons } from "../Interfaces";

// eslint-disable-next-line @typescript-eslint/no-unused-vars

//had to change from Enum since typescript no longer allows enums to have the same value for multiple items
const productIcons = ref<IProductIcons>({
  WebrootSoftwareInstallationCD: "webroot-icon",
  WebrootGamer: "webroot-icon",
  WebrootSecureVPN: "webroot-wifi-security",
  WebrootChromebook: "webroot-icon",
  WebrootAntiVirus: "webroot-icon",
  WebrootPlus: "webroot-icon",
  WebrootComplete: "webroot-icon",
  PersonalBasic: "carb-icon",
  PersonalPlus: "carb-icon",
  PersonalPrime: "carb-icon",
  WebrootPremium: "webroot-icon",
  WebrootPremiumFamily: "webroot-icon",
  AllstateIdentityProtectionAddOn: "icon-allstate",
  AllstateIdentityProtectionAddOnPremium: "icon-allstate",
  AllstateIdentityProtectionAddOnPremiumFamily: "icon-allstate",
  Webroot: "webroot-icon",
});

const buyFlowStore = useBuyFlowStore();
const computerName = ref<string>("");
const componentName = "CartItem";
const route = useRoute();
const showStrikeThroughPrice = ref<boolean>(false);
const fromCarbonite = computed(() => route.query.referralUrl?.toString().includes("carbonite"));

const props = defineProps({
  cartItem: {
    type: Object as PropType<ICartItem>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});

const quantity = ref(props.cartItem.quantity);
const bundledItems = ref<ICartItem[]>(props.cartItem?.bundledItems || []);
onMounted(() => {
  if (cartItem.value.computerId > 0) {
    computerName.value = getComputerName(cartItem.value.computerId);
  }
  selectedRatePlanId.value = cartItem.value.ratePlanId;
});

const cartItem = computed(() => {
  return props.cartItem;
});

//This needs to be set to empty string so the computed variables using it will trigger on load
const selectedRatePlanId = ref<string>("");

const product = computed(() => {
  return buyFlowStore.getCatalogItemBySku(cartItem.value.sku);
});

const hasSingleRatePlan = computed(() => {
  return product.value?.ratePlans.length === 1;
});

// Checks if the SKU is the Webroot Standalone SKU sold by Carbonite
// TODO: We should remove this when starting to sell Webroot via Zuora
const isStandAloneWebrootSku = computed(() => {
  return cartItem.value.sku === "Webroot";
});

const isMobileClient = computed(() => {
  return isMobile();
});

function numberOfMonths(ratePlanId) {
  return buyFlowStore.getRatePlanInfo(ratePlanId)?.months || DEFAULT_TERM_LENGTH_MONTHS;
}

const selectedRatePlan = computed(() => {
  return product.value?.ratePlans.find(r => r.months == numberOfMonths(cartItem.value?.ratePlanId));
});

const orderedRatePlans = computed(() => {
  return product.value?.ratePlans.filter(l => l.canPurchaseOnline).sort((a, b) => a.months - b.months);
});

const strikeThroughPrice = computed(() => {
  var catalogPrice = buyFlowStore.getRatePlanInfo(selectedRatePlanId.value)?.catalogPrice || 0;
  return catalogPrice * cartItem.value.quantity;
});

const isWebrootSku = computed(() => {
  return buyFlowStore.isWebrootSku(cartItem.value.sku);
});

function createId(prefix: string) {
  return `${prefix}_${props.index}`;
}

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

function getTermDescriptionForCatalog(catalog) {
  const termValue =
    catalog.months > DEFAULT_TERM_LENGTH_MONTHS
      ? catalog.months / DEFAULT_TERM_LENGTH_MONTHS
      : catalog.months === DEFAULT_TERM_LENGTH_MONTHS
      ? NUMERIC_YEAR
      : 0;

  // if termValue is zero, do not show anything.
  const termValueString = termValue === 0 ? "" : termValue.toString();

  const catalogTermType =
    catalog.months > DEFAULT_TERM_LENGTH_MONTHS
      ? terms.year
      : catalog.months === DEFAULT_TERM_LENGTH_MONTHS
      ? terms.year
      : terms.monthToMonth;

  if (isMobileClient.value) {
    return `${termValueString} ${t(`Common.${catalogTermType?.toString()}`)} ${t("CartComponent.subscription")}`;
  } else {
    const price = formatMoney(catalog.newSubscriptionPrice / termValue);
    return `${termValueString} ${t(`Common.${catalogTermType?.toString()}`)} ${t(
      "CartComponent.subscription"
    )} (${price} ${tn("perYear")})`;
  }
}

watch(quantity, debounce(updateQuantityInCart, 500));

function updateQuantityInCart() {
  const cartItem = buyFlowStore.cart?.items.find(i => i.ratePlanId === selectedRatePlanId.value);

  if (cartItem) {
    cartItem.quantity = quantity.value;
    buyFlowStore.updateShoppingCartTotal();
  }
}

function getIcon(sku: string): string {
  return productIcons.value[sku];
}

const showQuantity = computed(() => {
  return buyFlowStore.getMaxQuantityBySku(cartItem.value.sku) > 1 ? true : false;
});

function showStepper(cartItem) {
  // we want to shop the quantity stepper if the maximum quantity allowed is more than one and this is not a renewal
  // we also want to show the quantity stepper if the webroot product we're selling is the webroot standalone SKU sold on carbonite (that we're not selling any more) and we're on the email page
  //however, if we came from carbonite.com, then don't show the quantity selector regardless of the other conditions because the user has already picked their quantity at carbonite.com
  return (
    (!cartItem.computerId && showQuantity && !fromCarbonite.value) ||
    (isStandAloneWebrootSku.value && buyFlowStore.pageState === pageStates.email && !fromCarbonite.value)
  );
}

function showSelect() {
  // we want to shop the duration select if the duration allowed is more than one and this is not the the webroot standalone SKU old on carbonite
  // we also want to show the duration select if we're on the email page
  //however, if we came from carbonite.com, then don't show the duration select regardless of the other conditions because the user has already picked the duration at carbonite.com
  return (
    ((!hasSingleRatePlan.value && !isStandAloneWebrootSku.value) || buyFlowStore.pageState === pageStates.email) &&
    !fromCarbonite.value
  );
}

watch(selectedRatePlanId, () => {
  const item = buyFlowStore.cart?.items.find(i => i.ratePlanId === props.cartItem.ratePlanId);
  // showStrikeThroughPrice is getting calculated only cartItem update, now it will be calculated on onMounted event
  showStrikeThroughPrice.value = formatMoney(cartItem.value.total) != formatMoney(strikeThroughPrice.value);
  if (item && item.ratePlanId != selectedRatePlanId.value) {
    item.ratePlanId = selectedRatePlanId.value;
    buyFlowStore.updateShoppingCartTotal();
  }
});

//Need this watch to set the selected term after a remove happens
watch(cartItem, () => {
  selectedRatePlanId.value = cartItem.value.ratePlanId;
  //Javascript multiplication of 3 * 95.99, 6 * 95.99 gives 10 decimal values & cartItem.total(backend value) gives number rounding to two.
  showStrikeThroughPrice.value = formatMoney(cartItem.value.total) != formatMoney(strikeThroughPrice.value);
  updateBundleItems();
});

// As bundle item maybe deleted we should repopulate to show in bundle item component
function updateBundleItems() {
  bundledItems.value = [];
  if (product.value?.candyRackItems && product.value?.candyRackItems.length > 0) {
    for (const candyRackSku of product.value?.candyRackItems) {
      const candyRackItem = buyFlowStore.plans.find(x => x.sku === candyRackSku);
      if (candyRackItem && candyRackItem.candyRackSettings && candyRackItem.candyRackSettings.bundleWithCart) {
        const bundleItem = {
          sku: candyRackItem.sku,
          quantity: 1,
          total: candyRackItem.ratePlans[0].catalogPrice,
          ratePlanId: candyRackItem.ratePlans[0].ratePlanId,
        } as ICartItem;
        bundledItems.value.push(bundleItem);
      }
    }
  }
}
</script>
